import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useSession } from '../api/session';
import { OfflinePrefetchBoot } from '../offline/OfflinePrefetchBoot';

/**
 * Route guard. Only bounces to /login when the server has definitively said
 * "no user" - i.e. a successful fetch that returned user: null, AND we don't
 * have a last-known userId cached from a previous load.
 *
 * Transient errors (network blip, 5xx, SW cache miss while offline) must NOT
 * kick the user out: the offline-first cache still shows their groups/sets/
 * links, and kicking to /login would erase that experience.
 */
export function AuthGate({ children }: { children: ReactNode }): React.ReactElement {
  const { data, isLoading, isError, isSuccess } = useSession();

  // First paint before any resolution.
  if (isLoading && !data) return <div className="sl-loading">Loading…</div>;

  // Server returned a definitive answer.
  if (isSuccess) {
    if (data?.user) {
      return (
        <>
          <OfflinePrefetchBoot />
          {children}
        </>
      );
    }
    return <Navigate to="/login" replace />;
  }

  // Error or paused. Fall back to last-known signed-in signal so offline
  // reloads and flaky networks don't bounce the user to /login.
  const lastKnown = readLastUserId();
  if (isError && !lastKnown) return <Navigate to="/login" replace />;
  // Assume still signed in and render children; future mutations will surface
  // real auth failures (401) through their own error paths.
  return (
    <>
      <OfflinePrefetchBoot />
      {children}
    </>
  );
}

function readLastUserId(): string | null {
  try {
    return localStorage.getItem('sl.last-user-id');
  } catch {
    return null;
  }
}
